import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Save, Store, Percent, FileSpreadsheet, User, RefreshCw, AlertTriangle, Printer } from 'lucide-react';
import { useAuthStore } from '../../lib/auth';
import { useSettingsStore, useSync } from '../../hooks';
import { usePwaUpdate } from '../../hooks/usePwaUpdate';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, useToast } from '@lancar-pos/ui';
import { BUSINESS_TYPES, type BusinessType } from '@lancar-pos/shared';

export function SettingsPage() {
  const { user } = useAuthStore();
  const { getSetting, setSetting, isReady } = useSettingsStore();
  const {
    isSyncing,
    lastSyncAt,
    error: syncError,
    queueStats,
    isConfigured: syncConfigured,
    syncAll,
    pullFromSheets,
    pushToSheets,
    retryFailed,
  } = useSync();

  const {
    currentVersion,
    latestVersion,
    isChecking,
    hasUpdate,
    checkForUpdate,
    installUpdate,
    error,
  } = usePwaUpdate();

  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [taxEnabled, setTaxEnabled] = useState(true);
  const [taxRate, setTaxRate] = useState('11');
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);
  const [syncIntervalMinutes, setSyncIntervalMinutes] = useState('5');
  const [syncOnStartup, setSyncOnStartup] = useState(true);
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(true);
  const [receiptWidth, setReceiptWidth] = useState('58');
  const [receiptFooter, setReceiptFooter] = useState('');

  useEffect(() => {
    if (isReady) {
      setBusinessName(getSetting<string>('business_name') || '');
      setBusinessAddress(getSetting<string>('business_address') || '');
      setBusinessPhone(getSetting<string>('business_phone') || '');
      setBusinessType(getSetting<BusinessType>('business_type') ?? null);
      setTaxEnabled(getSetting<boolean>('tax_enabled') !== false);
      setTaxRate((getSetting<number>('tax_rate') || 11).toString());
      setAutoSyncEnabled(getSetting<boolean>('sync_auto_enabled') !== false);
      setSyncIntervalMinutes((getSetting<number>('sync_interval_minutes') || 15).toString());
      setSyncOnStartup(getSetting<boolean>('sync_on_startup') !== false);
      setAutoPrintEnabled(getSetting<boolean>('auto_print_enabled') !== false);
      setReceiptWidth((getSetting<number>('receipt_width') || 58).toString());
      setReceiptFooter(getSetting<string>('receipt_footer') || '');
    }
  }, [isReady]);

  const sheetsConfigured = getSetting<boolean>('sheets_configured') === true;
  const sheetsEmail = getSetting<string>('sheets_client_email') || '';

  const handleSave = async () => {
    if (!businessName.trim()) {
      toast.error('Nama usaha wajib diisi');
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all([
        setSetting('business_name', businessName),
        setSetting('business_address', businessAddress),
        setSetting('business_phone', businessPhone),
        setSetting('business_type', businessType),
        setSetting('tax_enabled', taxEnabled),
        setSetting('tax_rate', parseFloat(taxRate) || 0),
        setSetting('sync_auto_enabled', autoSyncEnabled),
        setSetting('sync_interval_minutes', parseInt(syncIntervalMinutes, 10) || 15),
        setSetting('sync_on_startup', syncOnStartup),
        setSetting('auto_print_enabled', autoPrintEnabled),
        setSetting('receipt_width', parseInt(receiptWidth, 10) || 58),
        setSetting('receipt_footer', receiptFooter),
      ]);

      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        toast.success('Berhasil disimpan', 'Pengaturan telah diperbarui');
      } else {
        toast.error('Gagal menyimpan beberapa pengaturan');
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      await syncAll();
      toast.success('Sinkronisasi berhasil');
    } catch (error) {
      toast.error('Sinkronisasi gagal', error instanceof Error ? error.message : 'Terjadi kesalahan');
    }
  };

  const handlePullFromSheets = async () => {
    try {
      await pullFromSheets();
      toast.success('Data berhasil ditarik dari Google Sheets');
    } catch (error) {
      toast.error('Gagal menarik data', error instanceof Error ? error.message : 'Terjadi kesalahan');
    }
  };

  const handlePushToSheets = async () => {
    try {
      await pushToSheets();
      toast.success('Data berhasil dikirim ke Google Sheets');
    } catch (error) {
      toast.error('Gagal mengirim data', error instanceof Error ? error.message : 'Terjadi kesalahan');
    }
  };

  const formatLastSyncAt = (date: string | null) => {
    if (!date) return 'Belum pernah';
    return new Intl.DateTimeFormat('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(date));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate({ to: '/app' })}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Pengaturan</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 lg:p-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary-600" />
              <CardTitle>Akun</CardTitle>
            </div>
            <CardDescription>Informasi akun Anda</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="text-sm font-medium text-gray-900">{user?.email}</p>
            </div>
            {user?.full_name && (
              <div>
                <p className="text-sm text-gray-500">Nama</p>
                <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
              </div>
            )}
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/app/forgot-password' })}
            >
              Ubah Kata Sandi
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-primary-600" />
              <CardTitle>Informasi Bisnis</CardTitle>
            </div>
            <CardDescription>Kelola informasi dasar bisnis Anda</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Nama Usaha"
              placeholder="Contoh: Toko Kopi Jaya"
              value={businessName}
              onChange={setBusinessName}
              required
            />
            <Input
              label="Alamat"
              placeholder="Contoh: Jl. Sudirman No. 123, Jakarta"
              value={businessAddress}
              onChange={setBusinessAddress}
            />
            <Input
              label="Nomor Telepon"
              placeholder="Contoh: 08123456789"
              value={businessPhone}
              onChange={setBusinessPhone}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Usaha</label>
              <select
                value={businessType || ''}
                onChange={(e) => setBusinessType(e.target.value as BusinessType || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Pilih jenis usaha</option>
                {BUSINESS_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-primary-600" />
              <CardTitle>Pajak</CardTitle>
            </div>
            <CardDescription>Pengaturan pajak penjualan</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="taxEnabledSettings"
                checked={taxEnabled}
                onChange={(e) => setTaxEnabled(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <label htmlFor="taxEnabledSettings" className="text-sm text-gray-700">
                Aktifkan pajak (PPN)
              </label>
            </div>
            {taxEnabled && (
              <Input
                label="Tarif Pajak (%)"
                type="number"
                placeholder="11"
                value={taxRate}
                onChange={setTaxRate}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-primary-600" />
              <CardTitle>Google Sheets</CardTitle>
            </div>
            <CardDescription>Sinkronisasi data ke Google Sheets</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Status Koneksi</p>
                <p className="text-sm text-gray-500">
                  {sheetsConfigured ? 'Terhubung' : 'Belum terhubung'}
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${sheetsConfigured
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
                  }`}
              >
                {sheetsConfigured ? 'Aktif' : 'Tidak Aktif'}
              </span>
            </div>

            {sheetsConfigured && sheetsEmail && (
              <div>
                <p className="text-sm text-gray-500">Service Account</p>
                <p className="text-sm text-gray-900 truncate">{sheetsEmail}</p>
              </div>
            )}

            <Button
              variant="outline"
              onClick={() => navigate({ to: '/app/sheets-setup' })}
            >
              {sheetsConfigured ? 'Hubungkan Ulang' : 'Hubungkan Google Sheets'}
            </Button>

            {syncConfigured && (
              <>
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="autoSyncEnabled"
                      checked={autoSyncEnabled}
                      onChange={(e) => setAutoSyncEnabled(e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <label htmlFor="autoSyncEnabled" className="text-sm text-gray-700">
                      Sinkronisasi otomatis
                    </label>
                  </div>

                  {autoSyncEnabled && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <label className="text-sm text-gray-700">Interval:</label>
                        <select
                          value={syncIntervalMinutes}
                          onChange={(e) => setSyncIntervalMinutes(e.target.value)}
                          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="5">5 menit</option>
                          <option value="10">10 menit</option>
                          <option value="15">15 menit (Disarankan)</option>
                          <option value="30">30 menit</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-500">
                        Interval lebih lama membantu menghindari batasan Google Sheets API (60 permintaan/menit)
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="syncOnStartup"
                      checked={syncOnStartup}
                      onChange={(e) => setSyncOnStartup(e.target.checked)}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <label htmlFor="syncOnStartup" className="text-sm text-gray-700">
                      Sinkronkan saat aplikasi dibuka
                    </label>
                  </div>

                  <div className="pt-2">
                    <p className="text-sm text-gray-500">Sinkronisasi terakhir</p>
                    <p className="text-sm font-medium text-gray-900">{formatLastSyncAt(lastSyncAt)}</p>
                  </div>
                </div>

                {syncError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Error</p>
                      <p className="text-xs text-red-600">{syncError}</p>
                    </div>
                  </div>
                )}

                {queueStats.failed > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">
                        {queueStats.failed} item gagal sinkron
                      </p>
                      <button
                        onClick={retryFailed}
                        className="text-xs text-amber-700 underline hover:no-underline"
                      >
                        Coba lagi
                      </button>
                    </div>
                  </div>
                )}

                {queueStats.pending > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      {queueStats.pending} item dalam antrean
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleSyncAll}
                    loading={isSyncing}
                    disabled={isSyncing}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                    Sinkronkan Sekarang
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handlePullFromSheets}
                    loading={isSyncing}
                    disabled={isSyncing}
                  >
                    Tarik Data
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handlePushToSheets}
                    loading={isSyncing}
                    disabled={isSyncing}
                  >
                    Kirim Data
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-primary-600" />
              <CardTitle>Printer</CardTitle>
            </div>
            <CardDescription>Pengaturan cetak struk</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="autoPrintEnabled"
                checked={autoPrintEnabled}
                onChange={(e) => setAutoPrintEnabled(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
              />
              <label htmlFor="autoPrintEnabled" className="text-sm text-gray-700">
                Cetak struk otomatis setelah checkout
              </label>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700">Lebar struk:</label>
              <select
                value={receiptWidth}
                onChange={(e) => setReceiptWidth(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="58">58 mm</option>
                <option value="80">80 mm</option>
              </select>
            </div>

            <div>
              <Input
                label="Teks Footer Struk"
                placeholder="Terima kasih! Kunjungi kami lagi."
                value={receiptFooter}
                onChange={setReceiptFooter}
              />
              <p className="text-xs text-gray-500 mt-1">
                Teks ini akan tampil di bagian bawah struk
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-primary-600" />
              <CardTitle>Versi Aplikasi</CardTitle>
            </div>
            <CardDescription>Periksa pembaruan aplikasi</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Versi saat ini</p>
              <p className="text-lg font-semibold text-gray-900">{currentVersion || 'Memuat...'}</p>
            </div>

            {latestVersion === null && (
              <Button
                variant="outline"
                onClick={checkForUpdate}
                loading={isChecking}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Cek Pembaruan
              </Button>
            )}

            {latestVersion !== null && !hasUpdate && (
              <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm font-medium">Anda sudah menggunakan versi terbaru</span>
              </div>
            )}

            {latestVersion !== null && hasUpdate && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 rounded-lg">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm font-medium">Pembaruan tersedia!</span>
                </div>
                <p className="text-sm text-gray-600">
                  Versi terbaru: <span className="font-semibold">{latestVersion}</span>
                </p>
                <Button
                  onClick={installUpdate}
                  className="w-full"
                >
                  Perbarui Sekarang
                </Button>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => navigate({ to: '/app' })}>
            Batal
          </Button>
          <Button onClick={handleSave} loading={loading}>
            <Save className="w-4 h-4 mr-2" />
            Simpan Perubahan
          </Button>
        </div>
      </main>
    </div>
  );
}
